import type { SurfaceProps } from './types';
import { surfaceFilled } from './types';

const SEVERITIES = ['Blocker', 'Critical', 'Major', 'Minor', 'Trivial'];
const PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
const FREQUENCIES = ['Always', 'Often', 'Sometimes', 'Rare'];

/**
 * Structured bug-report surface (the canonical QA artefact).
 * Вариант `base` (ядро · 8 полей) / `full` (+ приоритет · частота · влияние = 11 полей S07).
 * Скриншот/вложение — как ТЕКСТ «что приложить» (реальная загрузка файлов в песочнице
 * не делается; ценность — научить перечислять конкретный evidence, как учит S07).
 */
export function BugReportForm({ spec, value, disabled, onChange }: SurfaceProps) {
  const full = spec.config?.variant === 'full';
  const v: Record<string, string> = {
    title: '', description: '', severity: '', env: '', steps: '', expected: '', actual: '',
    attachments: '', priority: '', frequency: '', impact: '',
    ...(value as Record<string, string> | undefined),
  };
  const set = (patch: Record<string, string>) => {
    const next = { ...v, ...patch };
    onChange(next, surfaceFilled(spec, next));
  };

  return (
    <div className="surf surf-bug" data-testid="surf-bugreport" data-variant={full ? 'full' : 'base'}>
      <div className="surf-tag">Баг-репорт{full ? ' · полный' : ''}</div>

      <label className="bug-field">
        <span className="l">Заголовок</span>
        <input value={v.title} disabled={disabled} placeholder="Что и где сломалось — одной строкой"
          data-testid="bug-title" onChange={(e) => set({ title: e.target.value })} />
      </label>

      <label className="bug-field">
        <span className="l">Описание</span>
        <textarea value={v.description} disabled={disabled} placeholder="Суть в 1-2 предложениях: что за баг и почему это важно"
          data-testid="bug-description" onChange={(e) => set({ description: e.target.value })} />
      </label>

      <div className="bug-field">
        <span className="l">Серьёзность</span>
        <span className="sev">
          {SEVERITIES.map((s) => (
            <button type="button" key={s} className={v.severity === s ? 'on' : ''} disabled={disabled}
              onClick={() => set({ severity: s })}>{s}</button>
          ))}
        </span>
      </div>

      {full && (
        <div className="bug-field">
          <span className="l">Приоритет</span>
          <span className="sev">
            {PRIORITIES.map((p) => (
              <button type="button" key={p} className={v.priority === p ? 'on' : ''} disabled={disabled}
                data-testid={`bug-priority-${p}`} onClick={() => set({ priority: p })}>{p}</button>
            ))}
          </span>
        </div>
      )}

      <label className="bug-field">
        <span className="l">Окружение</span>
        <input value={v.env} disabled={disabled} placeholder="браузер / ОС / устройство / версия"
          onChange={(e) => set({ env: e.target.value })} />
      </label>

      <label className="bug-field">
        <span className="l">Шаги</span>
        <textarea value={v.steps} disabled={disabled} placeholder="1. … 2. … 3. …"
          onChange={(e) => set({ steps: e.target.value })} />
      </label>

      <label className="bug-field">
        <span className="l">Ожидаемо</span>
        <input value={v.expected} disabled={disabled} placeholder="что должно было быть"
          onChange={(e) => set({ expected: e.target.value })} />
      </label>

      <label className="bug-field">
        <span className="l">Фактически</span>
        <input value={v.actual} disabled={disabled} placeholder="что произошло"
          onChange={(e) => set({ actual: e.target.value })} />
      </label>

      {full && (
        <>
          <div className="bug-field">
            <span className="l">Частота</span>
            <span className="sev">
              {FREQUENCIES.map((f) => (
                <button type="button" key={f} className={v.frequency === f ? 'on' : ''} disabled={disabled}
                  onClick={() => set({ frequency: f })}>{f}</button>
              ))}
            </span>
          </div>
          <label className="bug-field">
            <span className="l">Влияние на бизнес</span>
            <input value={v.impact} disabled={disabled} placeholder="деньги / доверие / данные — конкретно, не «неудобно»"
              data-testid="bug-impact" onChange={(e) => set({ impact: e.target.value })} />
          </label>
        </>
      )}

      <label className="bug-field">
        <span className="l">Приложения</span>
        <input value={v.attachments} disabled={disabled} placeholder="что приложить: скриншот шага 2, лог, запрос — конкретно"
          data-testid="bug-attachments" onChange={(e) => set({ attachments: e.target.value })} />
      </label>
    </div>
  );
}
